import { Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { saveAs } from 'file-saver';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { getLeadReport } from '../../../API/lead';
import { getSurveyReport, PdfGenerateType } from '../../../API/surveys';
import { PdfReportTypes } from './motive.types';

interface Props {
  completed: boolean;
  surveyId: string;
  token: string;
  pdfReportType?: PdfReportTypes;
  compareProfileId?: string;
  size?: 'small' | 'medium' | 'large' | undefined;
  useCompanyColors?: boolean;
  isToolbarButton?: boolean;
}

const PdfReportButton: React.FC<Props> = ({
  pdfReportType = PdfReportTypes.MOTIVE,
  completed,
  surveyId,
  compareProfileId,
  size,
  useCompanyColors,
  isToolbarButton,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const [loading, setLoading] = useState<boolean>(false);
  const [generateType, setGenerateType] = useState<PdfGenerateType | ''>('');
  const [language, setLanguage] = useState<string>(''); // Initialize with an empty string

  const handleChangeGenerateType = (type: PdfGenerateType) => {
    return () => {
      setGenerateType(type);
      setLanguage(''); // Reset language selection when report type changes
    };
  };

  const handleLanguageChange = (lang: string) => {
    return () => {
      setLanguage(lang);
    };
  };

  const createAndOpenBlobGenerator = (currentSurveyId: string) => {
    if ((!generateType || !language) && pdfReportType === PdfReportTypes.MOTIVE) {
      return () => undefined;
    }
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      setLoading(true);
      const run = async () => {
        const result =
          pdfReportType === PdfReportTypes.MOTIVE
            ? await getSurveyReport(currentSurveyId, generateType as PdfGenerateType, compareProfileId, language)
            : await getLeadReport(currentSurveyId);
        const newBlob = new Blob([result], { type: 'application/pdf' });
        saveAs(newBlob, `Report-${currentSurveyId}`);
        if (result) {
          setLoading(false);
        }
      };
      run();
    };
  };

  const buttons = () => {
    return pdfReportType === PdfReportTypes.MOTIVE ? (
      <>
        <Button
          disabled={!completed || loading}
          size={size || 'medium'}
          variant={generateType === PdfGenerateType.EXPERT ? 'contained' : 'outlined'}
          style={{
            color: theme.palette.primary.main,
            borderColor: isToolbarButton ? '#fff' : undefined,
            marginRight: '1rem',
          }}
          onClick={handleChangeGenerateType(PdfGenerateType.EXPERT)}
        >
          <Typography variant="body1" noWrap>
            {t('expertSample')}
          </Typography>
        </Button>
        <Button
          disabled={!completed || loading}
          size={size || 'medium'}
          variant={generateType === PdfGenerateType.LEADERSHIP ? 'contained' : 'outlined'}
          style={{
            color: theme.palette.primary.main,
            borderColor: isToolbarButton ? '#fff' : undefined,
          }}
          onClick={handleChangeGenerateType(PdfGenerateType.LEADERSHIP)}
        >
          <Typography variant="body1" noWrap>
            {t('leadershipSample')}
          </Typography>
        </Button>
      </>
    ) : (
      <Button
        disabled={!completed || loading}
        size={size || 'medium'}
        variant={completed && !useCompanyColors ? 'outlined' : undefined}
        style={{
          color: theme.palette.primary.contrastText,
          borderColor: isToolbarButton ? '#f45f56' : undefined,
        }}
        onClick={createAndOpenBlobGenerator(surveyId)}
      >
        <Typography variant="body1" noWrap>
          {t('createReport')}
        </Typography>
      </Button>
    );
  };

  const languageButtons = () => (
    <>
      <Button
        disabled={!completed || loading}
        size={size || 'medium'}
        variant={language === 'de' ? 'contained' : 'outlined'}
        style={{
          marginRight: '1rem',
          color: theme.palette.primary.main,
          borderColor: isToolbarButton ? '#fff' : undefined,
        }}
        onClick={handleLanguageChange('de')}
      >
        <Typography variant="body1" noWrap>
          {t('German')}
        </Typography>
      </Button>
      <Button
        disabled={!completed || loading}
        size={size || 'medium'}
        variant={language === 'en' ? 'contained' : 'outlined'}
        style={{
          color: theme.palette.primary.main,
          borderColor: isToolbarButton ? '#fff' : undefined,
        }}
        onClick={handleLanguageChange('en')}
      >
        <Typography variant="body1" noWrap>
          {t('English')}
        </Typography>
      </Button>
    </>
  );

  return (
    <div className={classes.wrapper} style={{ margin: isToolbarButton ? 0 : theme.spacing(1) }}>
      {/* Step 1: Select Report Type */}
      {!generateType && !loading && completed && buttons()}

      {/* Step 2: Select Language */}
      {generateType && !language && !loading && completed && languageButtons()}

      {/* Step 3: Show Result Button */}
      {generateType && language && !loading && completed && (
        <Button
          disabled={!completed || loading}
          size={size || 'medium'}
          variant={completed && !useCompanyColors ? 'outlined' : undefined}
          style={{
            color:
              completed && !loading
                ? useCompanyColors
                  ? theme.palette.primary.main
                  : isToolbarButton
                  ? '#fff'
                  : 'green'
                : completed && loading
                ? 'grey'
                : useCompanyColors || isToolbarButton
                ? theme.palette.secondary.main
                : '#FFA500',
            borderColor: isToolbarButton ? '#fff' : undefined,
          }}
          onClick={completed ? createAndOpenBlobGenerator(surveyId) : undefined}
        >
          <Typography variant="body1" noWrap>
            {completed && !loading ? t('result') : completed && loading ? t('pdfCreate') : t('pending')}
          </Typography>
        </Button>
      )}

      {/* Loading Indicator */}
      {loading && completed && (
        <CircularProgress size={24} className={classes.buttonProgress} style={{ color: isToolbarButton ? '#fff' : undefined }} />
      )}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
}));

export default PdfReportButton;
